// src/pages/PricingPage.tsx

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LayoutAdmin from "../components/LayoutAdmin";

import axiosExt from '../axios';
//import { AxiosError } from "axios";
import { API_BASE_URL } from "../config.ts";
//import { useTranslation } from "react-i18next";

import {
    Customer,
    PricingFilter,
    Service,
    ServicePrice,
} from "../types";


//import { fetchServices, fetchCustomers } from "../api/apihelper";
//import { fetchCustomers } from "../api/customers";

const PricingPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = localStorage.getItem("token");
    // -----------------------------
    // Filters
    // -----------------------------
    const [filters, setFilters] = useState<PricingFilter>({
        serviceId: searchParams.get("serviceId"),
        customerId: searchParams.get("customerId"),
        from: null,
        to: null,
    });

    // -----------------------------
    // Data
    // -----------------------------
    const [prices, setPrices] = useState<ServicePrice[]>([]);
    const [services, setServices] = useState<Service[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(false);

    // -----------------------------
    // Dialog state
    // -----------------------------
    const [editing, setEditing] = useState<ServicePrice | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);


    const fetchServices = async () => {
        setLoading(true);
        try {

            const response = await axiosExt.get<Service[]>(`${API_BASE_URL}/services`, {
                headers: { "Authorization": `Bearer ${token}` },
            });
            setServices(response.data);
        } catch (error) {
            console.error("Error fetching services:", error);
        }
        finally {
            setLoading(false);
        }
    };

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const response = await axiosExt.get<Customer[]>(`${API_BASE_URL}/customers`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }

            );

            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }
            const sortedCustomers = [...response.data].sort((a, b) =>
                a.name.localeCompare(b.name)
            );

            setCustomers(sortedCustomers);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
        finally {
            setLoading(false);
        }
    };

    // -----------------------------
    // Load reference data
    // -----------------------------
    useEffect(() => {
        fetchServices();
        fetchCustomers();
    }, []);


    // -----------------------------
    // Load prices
    // -----------------------------
    const loadPrices = async () => {
        setLoading(true);

        const params: any = {};
        if (filters.serviceId) params.serviceId = filters.serviceId;
        if (filters.customerId) params.customerId = filters.customerId;
        if (filters.from) params.from = filters.from;
        if (filters.to) params.to = filters.to;

        try {
            
            const res = await axiosExt.get<ServicePrice[]>(
                `${API_BASE_URL}/pricing`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if(res.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }

            setPrices(res.data);
            setLoading(false);


        } catch (error) {
            console.error("Error fetching prices:", error);
        } finally {
            //setLoading(false);
        }

    };

    useEffect(() => {
        loadPrices();
    }, [filters]);

    // -----------------------------
    // Grid
    // -----------------------------
    const columns: GridColDef<ServicePrice>[] = useMemo(() => [
        {
            field: "serviceId",
            headerName: "Service",
            flex: 1,
        },
        {
            field: "customerId",
            headerName: "Customer",
            flex: 1,
            renderCell: (p) =>
                p.value ?? <em style={{ color: "#888" }}>Default</em>,
        },
        {
            field: "price",
            headerName: "Price",
            width: 120,
        },
        //{
        //    field: "validFrom",
        //    headerName: "Valid From",
        //    width: 140,
        //    valueFormatter: (p) => p.value?.substring(0, 10),
        //},
        //{
        //    field: "validTo",
        //    headerName: "Valid To",
        //    width: 140,
        //    valueFormatter: (p) => p.value ? p.value.substring(0, 10) : "—",
        //},
        {
            field: "actions",
            headerName: "",
            width: 120,
            renderCell: (p) => (
                <Button
                    size="small"
                    onClick={() => {
                        setEditing(p.row);
                        setDialogOpen(true);
                    }}
                >
                    Edit
                </Button>
            ),
        },
    ], []);

    // -----------------------------
    // Save
    // -----------------------------
    const savePrice = async () => {
        if (!editing) return;

        try {
            const token = localStorage.getItem("token");

            await axiosExt.post(
                `${API_BASE_URL}/pricing`,
                editing,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setDialogOpen(false);
            setEditing(null);
            loadPrices();
        } catch (err: any) {
            console.error("Failed to save price", err);

            const message =
                err?.response?.data?.message ??
                err?.response?.data ??
                "Failed to save pricing";

            alert(message);
        }
    };

    // -----------------------------
    // UI
    // -----------------------------
    return (
        <LayoutAdmin>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", flexGrow: 1 }}>

                <Box p={3}>
                    <Typography variant="h5" fontWeight="bold" mb={2}>
                        Pricing
                    </Typography>

                    {/* Filters */}
                    <Box display="flex" gap={2} mb={2}>
                        <TextField
                            select
                            label="Service"
                            value={filters.serviceId ?? ""}
                            onChange={(e) =>
                                setFilters(f => ({ ...f, serviceId: e.target.value || null }))
                            }
                            fullWidth
                        >
                            <MenuItem value="">All</MenuItem>
                            {services.map(s => (
                                <MenuItem key={s.id!} value={s.id!}>
                                    {s.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Customer"
                            value={filters.customerId ?? ""}
                            onChange={(e) =>
                                setFilters(f => ({ ...f, customerId: e.target.value || null }))
                            }
                            fullWidth
                        >
                            <MenuItem value="">All</MenuItem>
                            {customers.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            type="date"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) =>
                                setFilters(f => ({ ...f, from: e.target.value || null }))
                            }
                        />

                        <TextField
                            type="date"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) =>
                                setFilters(f => ({ ...f, to: e.target.value || null }))
                            }
                        />
                    </Box>

                    {/* Table */}
                    <DataGrid
                        rows={prices}
                        columns={columns}
                        getRowId={(r) => r.id}
                        loading={loading}
                        autoHeight
                        disableRowSelectionOnClick
                    />

                    {/* Add */}
                    <Box mt={2}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                const newPrice: ServicePrice = {
                                    id: crypto.randomUUID(),
                                    serviceId: filters.serviceId!,          // must be string
                                    customerId: filters.customerId ?? null, // normalize undefined → null
                                    price: 0,
                                    pricePerUnitAfter1stUnit: 0,
                                    validFrom: new Date().toISOString().substring(0, 10),
                                    validTo: null,
                                    currency: "EUR",
                                    isActive: true,                          // widened to boolean by typing
                                };

                                setEditing(newPrice);
                                setDialogOpen(true);
                            }}
                        >
                            Add Price
                        </Button>
                    </Box>

                    {/* Dialog */}
                    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Price</DialogTitle>

                        {editing && (
                            <DialogContent>
                                <TextField
                                    select
                                    label="Service"
                                    value={editing.serviceId}
                                    onChange={e =>
                                        setEditing({ ...editing, serviceId: e.target.value })
                                    }
                                    fullWidth
                                    margin="normal"
                                >
                                    {services.map(s => (
                                        <MenuItem key={s.id!} value={s.id!}>
                                            {s.name}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    select
                                    label="Customer"
                                    value={editing.customerId ?? ""}
                                    onChange={e =>
                                        setEditing({
                                            ...editing,
                                            customerId: e.target.value || null,
                                        })
                                    }
                                    fullWidth
                                    margin="normal"
                                >
                                    <MenuItem value="">Default</MenuItem>
                                    {customers.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    label="Price"
                                    type="number"
                                    value={editing.price}
                                    onChange={e =>
                                        setEditing({ ...editing, price: Number(e.target.value) })
                                    }
                                    fullWidth
                                    margin="normal"
                                />

                                <TextField
                                    type="date"
                                    label="Valid From"
                                    InputLabelProps={{ shrink: true }}
                                    value={editing.validFrom.substring(0, 10)}
                                    onChange={e =>
                                        setEditing({ ...editing, validFrom: e.target.value })
                                    }
                                    fullWidth
                                    margin="normal"
                                />

                                <TextField
                                    type="date"
                                    label="Valid To"
                                    InputLabelProps={{ shrink: true }}
                                    value={editing.validTo ?? ""}
                                    onChange={e =>
                                        setEditing({ ...editing, validTo: e.target.value || null })
                                    }
                                    fullWidth
                                    margin="normal"
                                />
                            </DialogContent>
                        )}

                        <DialogActions>
                            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button variant="contained" onClick={savePrice}>
                                Save
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Box>
        </LayoutAdmin>
    );
};

export default PricingPage;
